import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AppShellContext, loadTheme, THEME_KEY } from "./appShell";
import BottomNav from "./BottomNav";
import { IconMoon, IconSun, IconWifiOff } from "./icons";

/** Fond du navigateur (barre d'état mobile) aligné sur le thème actif. */
const THEME_COLORS = { dark: "#0a0b0d", light: "#f3f4f6" };

/**
 * Cadre commun à toutes les pages : largeur mobile, bascule de thème,
 * bandeau hors ligne / synchronisation et navigation basse persistante.
 */
export default function AppShell({ children }) {
  const [theme, setTheme] = useState(loadTheme);
  const [online, setOnline] = useState(
    typeof navigator !== "undefined" ? navigator.onLine : true
  );
  const [pending, setPending] = useState(0);

  /* Les jetons de couleur sont portés par <html data-theme>, pour que le fond
     de body suive aussi (la coquille ne couvre pas les zones de sur-défilement). */
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute("content", THEME_COLORS[theme]);
  }, [theme]);

  useEffect(() => {
    const goOnline = () => setOnline(true);
    const goOffline = () => setOnline(false);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, []);

  const toggleTheme = useCallback(() => {
    setTheme((prev) => {
      const next = prev === "dark" ? "light" : "dark";
      try {
        localStorage.setItem(THEME_KEY, next);
      } catch {
        /* mode privé : le thème ne sera juste pas mémorisé */
      }
      return next;
    });
  }, []);

  const ctx = useMemo(
    () => ({ theme, toggleTheme, online, pending, setPending }),
    [theme, toggleTheme, online, pending]
  );

  const showBanner = !online || pending > 0;
  const bannerText = !online
    ? pending > 0
      ? `Hors ligne · ${pending} en attente de synchronisation`
      : "Hors ligne · les scans seront synchronisés au retour du réseau"
    : `Synchronisation en cours · ${pending} en attente`;

  return (
    <AppShellContext.Provider value={ctx}>
      <div className="app-root">
        <button
          type="button"
          className="theme-toggle"
          onClick={toggleTheme}
          aria-label={theme === "dark" ? "Passer en thème clair" : "Passer en thème sombre"}
        >
          {theme === "dark" ? <IconMoon /> : <IconSun />}
        </button>

        {showBanner && (
          <div className={`sync-banner ${online ? "syncing" : "offline"}`}>
            <IconWifiOff />
            <span>{bannerText}</span>
          </div>
        )}

        <div className="app-content">{children}</div>

        <BottomNav />
      </div>
    </AppShellContext.Provider>
  );
}
